import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import {
  Add as AddIcon,
  UploadFile as ExportIcon,
  ReceiptLong,
  Person,
  Business,
  CalendarMonth,
  AttachMoney,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { salarySlipService } from "../../services";
import ActionMenu from "../../components/ui/ActionMenu";

const currentMonth = dayjs().format("YYYY-MM");

function ListPage() {
  const navigate = useNavigate();
  const [monthFilter, setMonthFilter] = useState(""); // Empty by default to show all slips
  const [salarySlips, setSalarySlips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSalarySlips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthFilter]);

  const fetchSalarySlips = async () => {
    setLoading(true);
    try {
      // Send empty string or null to get all slips (including general templates)
      const params = monthFilter ? { month: monthFilter } : {};
      const { data } = await salarySlipService.list(params);
      setSalarySlips(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load salary slips", err);
      toast.error("Failed to load salary slips");
      setSalarySlips([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (slip) => {
    const slipId = slip.id || slip._id;
    if (!slipId) {
      toast.error("Invalid salary slip ID");
      return;
    }
    if (
      !window.confirm(
        `Delete salary slip for ${slip.employee_name}? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await salarySlipService.remove(slipId);
      toast.success("Salary slip deleted");
      fetchSalarySlips();
    } catch (err) {
      console.error("Failed to delete salary slip", err);
      toast.error(err.response?.data?.error || "Failed to delete salary slip");
    }
  };

  const buildFallbackFilename = (slip) => {
    const name = (slip.employee_name || "Employee").replace(/\s+/g, " ").trim();
    const monthLabel = (slip.month ? dayjs(`${slip.month}-01`) : dayjs())
      .format("MMM YYYY")
      .toUpperCase();
    return `Pay Slips- ${name} ${monthLabel}.pdf`;
  };

  const resolveFilenameFromHeaders = (headers, fallback) => {
    const disposition = headers?.["content-disposition"];
    if (!disposition) {
      return fallback;
    }

    const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utfMatch && utfMatch[1]) {
      try {
        return decodeURIComponent(utfMatch[1]).trim();
      } catch (err) {
        console.warn("Failed to decode UTF-8 filename", err);
      }
    }

    const asciiMatch = disposition.match(/filename="?([^";]+)"?/i);
    if (asciiMatch && asciiMatch[1]) {
      return asciiMatch[1].trim();
    }

    return fallback;
  };

  const handleDownloadPdf = async (slip) => {
    try {
      const response = await salarySlipService.downloadPdf(slip.id || slip._id);
      const filename = resolveFilenameFromHeaders(
        response.headers,
        buildFallbackFilename(slip)
      );
      const blob = new Blob([response.data], {
        type: response.headers?.["content-type"] || "application/pdf",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(link.href);
      toast.success("PDF downloaded successfully");
    } catch (err) {
      console.error("Failed to download salary slip PDF", err);
      toast.error("Download failed");
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await salarySlipService.exportExcel(
        monthFilter ? { month: monthFilter } : {}
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `salary-slips-${monthFilter || "all"}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(link.href);
      toast.success("Excel exported successfully");
    } catch (err) {
      console.error("Failed to export salary slips", err);
      toast.error("Export failed");
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-6 sm:mb-8"
      >
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-2">Salary Slips</h1>
          <p className="text-sm sm:text-base text-gray-600">Manage employee salary slips</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <div className="w-full sm:w-48">
            <Input
              type="month"
              value={monthFilter}
              onChange={(event) => setMonthFilter(event.target.value || "")}
              placeholder="Filter by month (optional)"
            />
            {monthFilter && (
              <button
                onClick={() => setMonthFilter("")}
                className="text-xs text-primary hover:underline mt-1 block"
                type="button"
              >
                Clear filter (show all)
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-1 sm:flex-initial">
            <Button
              variant="secondary"
              onClick={handleExportExcel}
              className="flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <ExportIcon className="w-5 h-5" />
              <span className="hidden sm:inline">Export Excel</span>
              <span className="sm:hidden">Export</span>
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate("/salary-slip/create")}
              className="flex items-center justify-center gap-2 w-full sm:w-auto"
            >
              <AddIcon className="w-5 h-5" />
              <span className="hidden sm:inline">Create Salary Slip</span>
              <span className="sm:hidden">Create</span>
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Salary Slips Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card">
              <div className="flex items-center justify-between mb-4">
                <div className="w-16 h-16 skeleton rounded-2xl"></div>
                <div className="w-8 h-8 skeleton rounded-xl"></div>
              </div>
              <div className="h-6 skeleton rounded mb-2 w-3/4"></div>
              <div className="h-4 skeleton rounded w-full mb-2"></div>
              <div className="h-4 skeleton rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : salarySlips.length === 0 ? (
        <div className="card text-center py-12">
          <ReceiptLong className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No salary slips found</p>
        </div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
        >
          {salarySlips.map((slip, index) => {
            const slipId = slip.id || slip._id;
            if (!slipId) {
              console.warn("Salary slip missing ID:", slip);
              return null;
            }
            return (
            <motion.div
              key={slipId}
              variants={cardVariants}
              className="card group cursor-pointer overflow-hidden"
              whileHover={{ y: -8, transition: { duration: 0.2 } }}
              onClick={() => navigate(`/salary-slip/${slipId}`)}
            >
              <div className="flex items-start justify-between mb-4 gap-3">
                <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-lg flex-shrink-0">
                    <ReceiptLong className="w-6 h-6 sm:w-8 sm:h-8" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg sm:text-xl font-bold text-primary mb-1 truncate">
                      {slip.employee_name}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-1 truncate">
                      <Business className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">{slip.department}</span>
                    </p>
                  </div>
                </div>
                <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                  <ActionMenu
                    onView={() =>
                      navigate(`/salary-slip/${slipId}`)
                    }
                    onEdit={() =>
                      navigate(`/salary-slip/${slipId}/edit`)
                    }
                    onDownload={handleDownloadPdf}
                    onDelete={handleDelete}
                    item={slip}
                  />
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t border-gray-100">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3">
                  <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm min-w-0 flex-1">
                    <CalendarMonth className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600 truncate">{slip.month || "General Template"}</span>
                  </div>
                  <div className="flex items-center gap-2 min-w-0 flex-shrink-0 w-full sm:w-auto justify-end sm:justify-start">
                    <AttachMoney className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 flex-shrink-0" />
                    <span className="text-base sm:text-lg lg:text-xl xl:text-2xl font-bold text-primary break-words overflow-wrap-anywhere max-w-full">
                      ₹{Number(slip.net_salary || 0).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}

export default ListPage;
